import { Button } from "@/modern/components/ui/button-shadcn";
import { SharedModalTitle } from "@/modern/components/shared-modal-component";
import { Download, PenToolIcon, SaveIcon, Copy, Award } from "lucide-react";
import { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { isImageFileTyped } from "./FileHelpers";
import { getPresignedImageUrlForFile } from "@/network/network";
import { toast } from "sonner";

interface FileDetailHeaderProps {
  fileName: string;
  canView: boolean;
  canEditFile: boolean;
  isDirty: boolean;
  onDownload: () => void;
  openAnnotationDialog: () => void;
  onCreateSpotIncentive?: () => void;
  file: FormattedFile;
}

/**
 * File detail header with title and action buttons
 */
export const FileDetailHeader = ({ fileName, canView, canEditFile, isDirty, onDownload, openAnnotationDialog, onCreateSpotIncentive, file }: FileDetailHeaderProps) => {
  const handleCopyImage = async () => {
    if (!navigator.clipboard || !isImageFileTyped(file)) {
      return;
    }

    try {
      // Load image with CORS enabled to avoid tainted canvas
      const img = new Image();
      img.crossOrigin = "anonymous";

      const imageLoaded = new Promise((resolve, reject) => {
        img.onload = () => resolve(img);
        img.onerror = reject;
      });

      if (file.isCCImage) {
        img.src = file.originalImageUrl;
      } else {
        const response = await getPresignedImageUrlForFile(file.id);
        img.src = response.data.url;
      }
      await imageLoaded;

      // Convert image to PNG using canvas (PNG has better clipboard support)
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      ctx.drawImage(img, 0, 0);

      // Convert canvas to blob
      const pngBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

      if (!pngBlob) {
        throw new Error('Failed to convert canvas to blob');
      }

      // Write PNG image to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': pngBlob
        })
      ]);

      // Show success feedback
      toast.success("Image copied to clipboard!");
    } catch (error) {
      console.error("Error copying image:", error);
      toast.error("Unable to copy image to clipboard. Please try downloading instead.");
    }
  };
  return (
    <div className="flex flex-col gap-4 border-b px-6 py-5 md:flex-row md:items-center md:justify-between md:gap-6">
      <div className="min-w-0 space-y-3">
        <SharedModalTitle className="text-xl font-semibold leading-tight">
          <span className="block truncate">{fileName || "Untitled file"}</span>
        </SharedModalTitle>
      </div>

      <div className="flex flex-wrap items-center gap-4 pr-4">
        {onCreateSpotIncentive && (
          <Button type="button" variant="outline" onClick={onCreateSpotIncentive} className="shrink-0">
            <Award />
            Adjust Points
          </Button>
        )}
        {isImageFileTyped(file) && (
          <>
            <Button type="button" variant="outline" onClick={openAnnotationDialog} className="shrink-0">
              <PenToolIcon />
              Annotate
            </Button>
            {'clipboard' in navigator && (
              <Button type="button" variant="outline" onClick={handleCopyImage} className="shrink-0">
                <Copy />
                Copy Image
              </Button>
            )}
          </>
        )}
        <Button type="button" variant="outline" onClick={onDownload} disabled={!canView} className="shrink-0">
          <Download />
          Download
        </Button>

        {canEditFile && (
          <Button type="submit" disabled={!isDirty} className="shrink-0">
            <SaveIcon />
            Save changes
          </Button>
        )}
      </div>
    </div>
  );
};
